import { createTheme } from "@mui/material";
import { resolveTokens } from "./tokens";
import tlscareJson from "./tlscare.tokens.json";

// One MUI theme per brand. gpscare is exactly the theme the app shipped with
// before TLS-14 (palette only); every TLScare override lives in this file so
// nothing leaks into GPS Care.
export function buildMuiTheme(brandKey) {
  const tokens = resolveTokens(brandKey);
  const palette = {
    primary: { main: tokens.colors["primary"] },
    secondary: { main: tokens.colors["secondary"] },
  };

  if (brandKey !== "tlscare") {
    return createTheme({ palette });
  }

  const { brand, interactive, colors, radius, font } = tlscareJson;
  const focusRing = `0 0 0 3px ${interactive["focus-ring"]}`;

  return createTheme({
    palette: {
      primary: {
        main: brand["500"],
        light: brand["100"],
        dark: brand["600"],
        contrastText: "#FFFFFF",
      },
      secondary: { main: colors["secondary"], contrastText: "#FFFFFF" },
      text: { primary: colors["text"], secondary: colors["text-body"], disabled: colors["text-muted"] },
      background: { default: colors["bg-color"], paper: colors["surface"] },
      divider: colors["border"],
    },
    shape: { borderRadius: radius.control },
    typography: {
      fontFamily: font.family,
      fontSize: tokens.type["default"],
      h1: { fontFamily: font.heading, fontWeight: 800 },
      h2: { fontFamily: font.heading, fontWeight: 800 },
      h3: { fontFamily: font.heading, fontWeight: 700 },
      h4: { fontFamily: font.heading, fontWeight: 700 },
      h5: { fontFamily: font.heading, fontWeight: 700 },
      h6: { fontFamily: font.heading, fontWeight: 700 },
      button: { textTransform: "none", fontWeight: 600 },
    },
    components: {
      MuiButtonBase: { defaultProps: { disableRipple: false } },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: radius.control,
            transition: "background-color 150ms ease-out, border-color 150ms ease-out, box-shadow 150ms ease-out",
            "&:focus-visible": { boxShadow: focusRing },
          },
          containedPrimary: {
            "&:hover": { backgroundColor: interactive["primary-hover"] },
            "&:active": { backgroundColor: interactive["primary-pressed"] },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.control,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: colors["border-input"] },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: brand["500"] },
            "&.Mui-focused": { boxShadow: focusRing },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: brand["500"], borderWidth: 1 },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { height: 3, borderRadius: "3px 3px 0 0", backgroundColor: brand["500"] },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            "&.Mui-selected": { color: brand["600"] },
          },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
        styleOverrides: {
          tooltip: {
            backgroundColor: "#0d2137",
            color: "#FFFFFF",
            fontSize: 12,
            borderRadius: radius.small,
          },
          arrow: { color: "#0d2137" },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: radius.card, boxShadow: "0 12px 32px rgba(13, 33, 55, 0.12)" },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: { "&:not(.MuiBackdrop-invisible)": { backgroundColor: "rgba(13, 33, 55, 0.5)" } },
        },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { fontSize: 18, fontWeight: 700, color: colors["heading"] } },
      },
      MuiDialogActions: {
        styleOverrides: { root: { gap: 8 } },
      },
      MuiLink: { styleOverrides: { root: { color: brand["500"] } } },
    },
  });
}
